import Navigation from '../../../components/feature/Navigation';
import Footer from '../../../components/feature/Footer';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useSEO } from '../../../utils/seo';

const BlogPostPage = () => {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  useSEO({
    title: 'The Future of Global Construction Supply Chains | Go Global Inc.',
    description: 'How digital transformation and direct factory relationships are revolutionizing the way large-scale projects source materials worldwide.',
    keywords: 'construction supply chains, digital transformation, factory relationships, material sourcing, global logistics',
    canonical: `${siteUrl}/blog/future-of-global-construction-supply-chains`,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: 'The Future of Global Construction Supply Chains',
      description: 'How digital transformation and direct factory relationships are revolutionizing the way large-scale projects source materials worldwide.',
      author: {
        '@type': 'Person',
        name: 'Michael Chen',
      },
      datePublished: '2025-01-15',
      dateModified: '2025-01-15',
      publisher: {
        '@type': 'Organization',
        name: 'Go Global Inc.',
        logo: {
          '@type': 'ImageObject',
          url: `${siteUrl}/favicon.ico`,
        },
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `${siteUrl}/blog/future-of-global-construction-supply-chains`,
      },
    },
  });

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-12 lg:pt-40 lg:pb-16 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-4xl mx-auto px-6 lg:px-12">
            <Link to="/blog" className="inline-flex items-center text-primary-600 font-medium hover:text-primary-700 transition-colors mb-8 cursor-pointer">
              <i className="ri-arrow-left-line mr-2"></i>
              Back to Blog
            </Link>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="text-xs font-semibold text-primary-600 uppercase tracking-wide mb-4">
                Industry Insights
              </div>
              <h1 className="font-display font-semibold text-4xl md:text-5xl lg:text-6xl text-gray-900 leading-tight mb-6">
                The Future of Global Construction Supply Chains
              </h1>
              <div className="flex items-center space-x-4 text-gray-600 mb-8">
                <span>Michael Chen</span>
                <span>•</span>
                <span>January 15, 2025</span>
                <span>•</span>
                <span>8 min read</span>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Featured Image */}
        <section className="pb-16">
          <div className="max-w-6xl mx-auto px-6 lg:px-12">
            <div className="relative h-96 lg:h-[600px] rounded-2xl overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/c9a327618d403f7b45dad3edde554942.jpg"
                alt="Global Construction Supply Chains"
                className="w-full h-full object-cover object-center"
              />
            </div>
          </div>
        </section>

        {/* Article Content */}
        <section className="pb-20 lg:pb-28">
          <div className="max-w-4xl mx-auto px-6 lg:px-12">
            <article className="prose prose-lg max-w-none">
              <p className="text-xl text-gray-700 leading-relaxed mb-8">
                The construction industry is experiencing a fundamental shift in how materials are sourced, managed, and delivered to project sites around the world. Digital transformation and direct factory relationships are revolutionizing traditional supply chain models, creating unprecedented opportunities for cost savings and efficiency gains.
              </p>

              <h2 className="font-display font-semibold text-3xl text-gray-900 mt-12 mb-6">
                The Digital Revolution in Construction Procurement
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                Traditional construction supply chains have long been characterized by multiple intermediaries, opaque pricing structures, and lengthy lead times. However, digital platforms are now enabling direct connections between contractors and manufacturers, eliminating unnecessary middlemen and providing real-time visibility into inventory, pricing, and shipping timelines.
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                Advanced analytics and AI-powered forecasting tools allow project managers to anticipate material needs with greater accuracy, reducing waste and preventing costly delays. Cloud-based collaboration platforms enable seamless communication between all stakeholders, from architects and engineers to suppliers and logistics providers.
              </p>

              <h2 className="font-display font-semibold text-3xl text-gray-900 mt-12 mb-6">
                Direct Factory Relationships: A Game Changer
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                Establishing direct relationships with overseas manufacturers has become increasingly viable for medium to large-scale construction projects. By bypassing traditional distributors, contractors can achieve cost savings of 20-40% while maintaining or even improving quality standards through direct oversight of production processes.
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                These direct relationships also provide greater flexibility in customization, allowing builders to specify exact dimensions, finishes, and performance characteristics without the constraints imposed by standardized catalog offerings. Furthermore, direct communication with factory engineers enables rapid problem-solving and adaptation to changing project requirements.
              </p>

              <h2 className="font-display font-semibold text-3xl text-gray-900 mt-12 mb-6">
                Overcoming Logistics Challenges
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                While the benefits of global sourcing are substantial, navigating international logistics remains complex. Successful implementation requires expertise in customs regulations, freight forwarding, and risk management. Professional supply chain partners with established networks and proven track records become invaluable in ensuring smooth operations.
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                Container optimization, consolidated shipping, and strategic warehousing solutions can significantly reduce transportation costs and carbon footprints. Advanced tracking systems provide real-time updates on shipment locations and estimated arrival times, enabling better coordination with construction schedules.
              </p>

              <h2 className="font-display font-semibold text-3xl text-gray-900 mt-12 mb-6">
                Sustainability and Ethical Sourcing
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                Modern construction supply chains increasingly prioritize sustainability and ethical manufacturing practices. Digital traceability systems allow contractors to verify the origin of materials, ensure compliance with environmental standards, and support suppliers committed to fair labor practices.
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                Many forward-thinking developers now recognize that sustainable sourcing not only aligns with corporate responsibility goals but also mitigates regulatory risks and enhances brand reputation. The ability to demonstrate responsible supply chain practices has become a competitive advantage in securing projects and attracting environmentally conscious clients.
              </p>

              <h2 className="font-display font-semibold text-3xl text-gray-900 mt-12 mb-6">
                Looking Ahead: The Next Decade
              </h2>
              <p className="text-gray-700 leading-relaxed mb-6">
                As technology continues to advance, we can expect even greater integration between design, procurement, and construction phases. Building Information Modeling (BIM) systems will increasingly connect with supply chain platforms, enabling automatic material ordering based on design specifications and project schedules.
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                Blockchain technology promises enhanced transparency and security in international transactions, while artificial intelligence will optimize routing decisions and predict potential supply chain disruptions before they impact projects. The construction companies that embrace these innovations today will be best positioned to thrive in tomorrow's competitive landscape.
              </p>
              <p className="text-gray-700 leading-relaxed mb-6">
                The future of global construction supply chains is bright, offering unprecedented opportunities for those willing to adapt to new models and technologies. By combining digital tools with strategic partnerships and direct factory relationships, contractors can achieve the trifecta of reduced costs, improved quality, and faster project delivery.
              </p>
            </article>

            {/* Author Bio */}
            <div className="mt-16 pt-8 border-t border-gray-200">
              <div className="flex items-start space-x-4">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary-600 to-secondary-600 flex items-center justify-center text-white text-2xl font-semibold">
                  MC
                </div>
                <div>
                  <h3 className="font-display font-semibold text-xl text-gray-900 mb-2">Michael Chen</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Michael is a supply chain consultant with over 15 years of experience in global construction logistics. He specializes in helping contractors optimize their procurement processes and establish direct factory relationships.
                  </p>
                </div>
              </div>
            </div>

            {/* Related Posts */}
            <div className="mt-16 pt-8 border-t border-gray-200">
              <h3 className="font-display font-semibold text-2xl text-gray-900 mb-8">Related Articles</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <Link to="/blog/reduce-construction-material-costs" className="group cursor-pointer">
                  <div className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-all duration-300">
                    <div className="text-xs font-semibold text-primary-600 uppercase tracking-wide mb-2">
                      Cost Management
                    </div>
                    <h4 className="font-display font-semibold text-lg text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                      5 Ways to Reduce Construction Material Costs Without Compromising Quality
                    </h4>
                    <p className="text-sm text-gray-600">Proven strategies for optimizing your material procurement process...</p>
                  </div>
                </Link>
                <Link to="/blog/navigating-international-customs" className="group cursor-pointer">
                  <div className="bg-gray-50 rounded-xl p-6 hover:shadow-lg transition-all duration-300">
                    <div className="text-xs font-semibold text-primary-600 uppercase tracking-wide mb-2">
                      Logistics
                    </div>
                    <h4 className="font-display font-semibold text-lg text-gray-900 mb-2 group-hover:text-primary-600 transition-colors">
                      Navigating International Customs: A Complete Guide for Contractors
                    </h4>
                    <p className="text-sm text-gray-600">Everything you need to know about customs clearance and compliance...</p>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default BlogPostPage;